import { z } from "zod";

import { CategoryAutocompleter } from "./GbpCategoriesInput/CategoryAutocompleter";
import i18n from "../../../../i18n";
import { LocationData } from "../../../../models/LocationData";

const t = (key: string) =>
  i18n.t(`facilityDetailsPage.modalEditProfile.categoryForm.errors.${key}`);

const categorySchema = z.object({
  name: z
    .string()
    .trim()
    .refine(() => CategoryAutocompleter.current !== undefined, {
      message: t("category_list_not_yet_initialized")
    })
    .refine((value) => value.length > 0, { message: t("empty_category") })
    .refine(
      (value) =>
        !!value && CategoryAutocompleter.current?.checkIsValidCategory(value),
      { message: t("invalid_category") }
    )
});

export const schema = z
  .object({
    primaryCategory: categorySchema,
    additionalCategories: z.array(categorySchema).max(9)
  })
  .superRefine((data, ctx) => {
    const allCategories = [
      data.primaryCategory,
      ...data.additionalCategories
    ].map((item) => item.name);
    const uniqueCategories = new Set(allCategories);
    const hasDuplicates = allCategories.length !== uniqueCategories.size;
    if (hasDuplicates) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("duplicate_category"),
        path: ["additionalCategories"]
      });
      return;
    }
  });

export type Schema = z.infer<typeof schema>;

export const generateDefaultValues = (data: LocationData): Schema => {
  const mainCategory = data.categories.find((category) => category.is_main);
  const mainCategoryName = mainCategory?.gbp_category_name ?? "";

  return {
    primaryCategory: {
      name: mainCategoryName
    },
    additionalCategories: data.categories
      .filter((category) => !category.is_main)
      .map(({ gbp_category_name }) => ({
        name: gbp_category_name
      }))
  };
};
